import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import OverlayDialog from '../../app/common/components/OverlayDialog';
import CheckboxLink from '../../app/common/components/CheckboxLink';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import Button from '../../app/common/components/Button';
import { history } from '../../utils/history';
import ErrorMessage from '../../app/common/components/errorMessage/ErrorMessage';
import { COLORS } from '../../app/common/constants/styles';
import {
    BottomTitle,
    ButtonWrapper,
    CancelSpace,
    DeleteLinkWrapper,
    Description,
    MarkdownWrapper,
    Space40,
    SubWrapper,
    Title3,
    Title5,
    TopDescription,
    Title2,
    TopTitleWrapper,
    UlLi,
    UlLiLink,
    UlWrapper,
    UnderLine,
    Wrapper,
} from './cancelMemberStyles';
import DeleteConfirmation from './DeleteConfirmation';
import { IMemberInfo } from './cancelMemberState';
import {
    destroyChallenge,
    startChallenge,
    updateChallengerLanguageWithOutStop,
} from '../../utils/challengerMain';
import { SupportedLanguage } from '../../config/constants/supportedLanguage.enum';
import OverlayHelper from '../../utils/overlayHelper';
import { PageId } from '../../config/constants/pageId.enum';
import CancelMembershipMarkDown from './CancelMembershipMarkDown';
import HeadTitle from '../../app/common/components/HeadTitle';

export interface ICancelMembershipFormState {
    isJapanChecked: boolean;
    isOverseasChecked: boolean;
    japanError: ReactNode;
    overseasError: ReactNode;
    memberLoggedIn: boolean;
    openOverlayDialog: boolean;
    policyTitleId: string;
}

export interface ICancelMembershipProps {
    openCancelMembershipOverlay: () => void;
    closeCancelMembershipOverlay: () => void;
    cancelMemberGoBack: () => void;
    deleteAccount: (member: IMemberInfo, challengerPid: string, isChallengeLoaded: boolean) => void;
    startDeleteAccount: () => void;
    member: IMemberInfo;
    intl: InjectedIntl;
    cancelMembershipOverlayOpen: boolean;
    errorDeletingAccount: any;
    deleting: boolean;
    externalPointUrl: string;
    twExternalPointUrl?: string;
    memberLoggedIn: boolean;
    challengerPid: string;
    locale: SupportedLanguage;
    contentPrefix?: string;
    clientPrefix?: string;
    customExtPoint?: string;
}

export const POLICY_JAPAN_TITLE_ID = 'cancel.membership.subBottomTitle1';
const POLICY_GLOBAL_TITLE_ID = 'cancel.membership.subBottomTitle2';

class CancelMembership extends React.Component<ICancelMembershipProps, ICancelMembershipFormState> {
    private isChallengeLoaded: boolean;

    frameRef: HTMLIFrameElement | null = null;

    constructor(props: ICancelMembershipProps) {
        super(props);

        this.state = {
            isJapanChecked: false,
            isOverseasChecked: false,
            japanError: null,
            overseasError: null,
            memberLoggedIn: props.memberLoggedIn,
            openOverlayDialog: false,
            policyTitleId: POLICY_JAPAN_TITLE_ID,
        };
        this.isChallengeLoaded = !this.props.challengerPid;

        this.openDeleteForm = this.openDeleteForm.bind(this);
        this.onAcceptDelete = this.onAcceptDelete.bind(this);
        this.closeInfoDialog = this.closeInfoDialog.bind(this);
    }

    public componentDidMount() {
        window.scrollTo({ top: 0 });
        const { challengerPid } = this.props;
        startChallenge(challengerPid);
    }

    public componentWillUnmount() {
        const { challengerPid } = this.props;
        destroyChallenge(challengerPid);
        OverlayHelper.closeHelper(PageId.cancel_membership);
    }

    public componentDidUpdate(prevProps: ICancelMembershipProps): void {
        const { locale } = this.props;
        if (prevProps.locale !== locale) {
            updateChallengerLanguageWithOutStop(locale);
        }
    }

    static getDerivedStateFromProps(
        props: ICancelMembershipProps,
        state: ICancelMembershipFormState,
    ) {
        if (props.memberLoggedIn !== state.memberLoggedIn) {
            return null;
        }
        const { memberLoggedIn } = props;
        return { ...state, memberLoggedIn };
    }

    public onAcceptDelete(challengerPid: string) {
        this.props.deleteAccount(this.props.member, challengerPid, this.isChallengeLoaded);
    }

    public getError(type: string): ReactNode {
        return <ErrorMessage message={TranslationHelpers.getMessageDescriptor(type)} />;
    }

    public openDeleteForm(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
        event.preventDefault();

        const { openCancelMembershipOverlay, contentPrefix } = this.props;
        const { isJapanChecked, isOverseasChecked } = this.state;

        const prefix = contentPrefix ? `${contentPrefix}.` : '';
        if (isJapanChecked && isOverseasChecked) {
            openCancelMembershipOverlay();
            this.props.startDeleteAccount();
        } else {
            if (!isJapanChecked) {
                const japanError = this.getError(`${prefix}cancel.membership.japanServiceError`);
                this.setState({ japanError });
            }
            if (!isOverseasChecked) {
                const overseasError = this.getError(
                    `${prefix}cancel.membership.overseasServiceError`,
                );
                this.setState({ overseasError });
            }
        }
    }

    public closeInfoDialog() {
        if (this.state.openOverlayDialog) {
            this.setState({
                openOverlayDialog: false,
                policyTitleId: POLICY_JAPAN_TITLE_ID,
            });
        }
    }

    public render() {
        const { isJapanChecked, isOverseasChecked } = this.state;
        const {
            closeCancelMembershipOverlay,
            cancelMemberGoBack,
            cancelMembershipOverlayOpen,
            errorDeletingAccount,
            member,
            externalPointUrl,
            challengerPid,
            twExternalPointUrl,
            contentPrefix,
            clientPrefix,
            customExtPoint,
            intl,
        } = this.props;
        const prefix = contentPrefix ? `${contentPrefix}.` : '';
        const isJapanClient = !prefix;
        const username = member.username ?? member.email;
        const { japanError, overseasError, openOverlayDialog, policyTitleId } = this.state;
        const clientConfig = `${localStorage.getItem('ACTIVE_CLIENT_ID')}.`;
        let ActiveClientPrefix;
        if (clientPrefix && clientPrefix.length !== 0) {
            for (let index = 0; index < clientPrefix.length; index++) {
                if (clientConfig?.includes(clientPrefix[index])) {
                    ActiveClientPrefix = clientPrefix[index];
                    break;
                }
            }
        }

        return (
            <Wrapper>
                <OverlayDialog
                    title={TranslationHelpers.getMessageDescriptor(
                        'rakservice.delete_confirmation.title.line',
                    )}
                    username={username}
                    isOpen={cancelMembershipOverlayOpen}
                    onClose={closeCancelMembershipOverlay}
                >
                    <DeleteConfirmation
                        onAccept={this.onAcceptDelete}
                        onClose={closeCancelMembershipOverlay}
                        apiError={errorDeletingAccount}
                        deleting={this.props.deleting}
                        challengerPid={challengerPid}
                        challengerReadyCallback={(isChallengeLoaded: boolean) => {
                            this.isChallengeLoaded = isChallengeLoaded;
                        }}
                    />
                </OverlayDialog>
                <OverlayDialog
                    title={TranslationHelpers.getMessageDescriptor(`${prefix}${policyTitleId}`)}
                    isOpen={openOverlayDialog}
                    onClose={this.closeInfoDialog}
                    isLarge={true}
                >
                    <MarkdownWrapper>
                        <CancelMembershipMarkDown policyTitleId={policyTitleId} />
                    </MarkdownWrapper>
                    <ButtonWrapper>
                        <Button
                            id="policyClose"
                            type="button"
                            width="25%"
                            primary={true}
                            text={TranslationHelpers.getMessageDescriptor('button.back')}
                            onClick={this.closeInfoDialog}
                            loading={false}
                            disabled={false}
                            qaId="policyClose"
                            success={false}
                        />
                    </ButtonWrapper>
                </OverlayDialog>
                <HeadTitle />
                <TopTitleWrapper>
                    <Title2>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.top.title`,
                        )}
                    </Title2>
                    <TopDescription>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.top.description`,
                        )}
                    </TopDescription>
                </TopTitleWrapper>
                <SubWrapper>
                    <Title3>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.subTitle1`,
                        )}
                    </Title3>
                    <SubWrapper>
                        <UlWrapper>
                            <UlLi>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.topPartOneUlOne`,
                                )}
                            </UlLi>
                            <UlLi>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.topPartOneUlTwo`,
                                )}
                            </UlLi>
                            <UlLi>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.topPartOneUlThree`,
                                )}
                            </UlLi>
                            {isJapanClient && (
                                <>
                                    <UlLi>
                                        {TranslationHelpers.getFormattedMessage(
                                            'cancel.membership.topPartOneUlFour',
                                        )}
                                    </UlLi>
                                    <UlLi>
                                        {TranslationHelpers.getFormattedMessage(
                                            'cancel.membership.topPartOneUlFive',
                                            {
                                                pravicyUrl: (
                                                    <UlLiLink
                                                        href={intl.formatMessage(
                                                            TranslationHelpers.getMessageDescriptor(
                                                                `cancel.membership.pravicyUrl`,
                                                            ),
                                                        )}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {TranslationHelpers.getFormattedMessage(
                                                            `cancel.membership.pravicyUrl`,
                                                        )}
                                                    </UlLiLink>
                                                ),
                                            },
                                        )}
                                    </UlLi>
                                </>
                            )}
                        </UlWrapper>
                    </SubWrapper>
                </SubWrapper>
                <SubWrapper>
                    <Title3>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.subTitle2`,
                        )}
                    </Title3>

                    <SubWrapper>
                        <UlWrapper>
                            <UlLi>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.topPartTwoUlOne`,
                                )}
                            </UlLi>
                            <UlLi>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.topPartTwoUlTwo`,
                                )}
                            </UlLi>
                            <UlLi>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.topPartTwoUlThree`,
                                )}
                            </UlLi>
                            <UlLi>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.externalPointUrl`,
                                    {
                                        TaiwanPointUrl: (
                                            <UlLiLink
                                                href={twExternalPointUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {TranslationHelpers.getFormattedMessage(
                                                    `${prefix}cancel.membership.TaiwanPointUrl`,
                                                )}
                                            </UlLiLink>
                                        ),
                                        JapanPointUrl: (
                                            <UlLiLink
                                                href={externalPointUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {TranslationHelpers.getFormattedMessage(
                                                    `${prefix}cancel.membership.JapanPointUrl`,
                                                )}
                                            </UlLiLink>
                                        ),
                                    },
                                )}
                            </UlLi>
                        </UlWrapper>
                    </SubWrapper>
                </SubWrapper>
                {ActiveClientPrefix && (
                    <SubWrapper>
                        <Title3>
                            {TranslationHelpers.getFormattedMessage(
                                `${ActiveClientPrefix}.cancel.membership.subTitle3`,
                            )}
                        </Title3>

                        <SubWrapper>
                            <UlWrapper>
                                <UlLi>
                                    {TranslationHelpers.getFormattedMessage(
                                        `${ActiveClientPrefix}.cancel.membership.topPartThreeUlOne`,
                                    )}
                                </UlLi>
                                <UlLi>
                                    {TranslationHelpers.getFormattedMessage(
                                        `${ActiveClientPrefix}.cancel.membership.topPartThreeUlTwo`,
                                    )}
                                </UlLi>
                                <UlLi>
                                    {TranslationHelpers.getFormattedMessage(
                                        `${ActiveClientPrefix}.cancel.membership.topPartThreeUlThree`,
                                    )}
                                </UlLi>
                                <UlLi>
                                    {TranslationHelpers.getFormattedMessage(
                                        `${ActiveClientPrefix}.cancel.membership.externalPointUrl`,
                                        {
                                            JapanPointUrl: (
                                                <UlLiLink
                                                    href={customExtPoint}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {TranslationHelpers.getFormattedMessage(
                                                        `${ActiveClientPrefix}.cancel.membership.JapanPointUrl`,
                                                    )}
                                                </UlLiLink>
                                            ),
                                        },
                                    )}
                                </UlLi>
                            </UlWrapper>
                        </SubWrapper>
                    </SubWrapper>
                )}
                <SubWrapper>
                    <Description>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.topPartThree`,
                        )}
                    </Description>
                </SubWrapper>
                <UnderLine></UnderLine>
                <SubWrapper>
                    <BottomTitle>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.bottomTitle`,
                        )}
                    </BottomTitle>
                </SubWrapper>
                <SubWrapper>
                    <Description>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.bottomTopline1`,
                        )}
                    </Description>
                    <Description>
                        {TranslationHelpers.getFormattedMessage(
                            `${prefix}cancel.membership.bottomTopline2`,
                        )}
                    </Description>
                </SubWrapper>
                <SubWrapper>
                    <SubWrapper>
                        <Title5>
                            {TranslationHelpers.getFormattedMessage(
                                `${prefix}cancel.membership.subBottomTitle1`,
                            )}
                        </Title5>
                        <CancelSpace />
                        <CheckboxLink
                            id="japanService"
                            value={isJapanChecked}
                            checked={isJapanChecked}
                            label={`${prefix}cancel.membership.checkboxJapanService`}
                            onChange={() => {
                                const isJapanChecked = !this.state.isJapanChecked;
                                this.setState({ isJapanChecked });
                                if (isJapanChecked) {
                                    this.setState({
                                        openOverlayDialog: true,
                                        policyTitleId: POLICY_JAPAN_TITLE_ID,
                                    });
                                }
                            }}
                        />
                        {japanError && !isJapanChecked && japanError}
                        <Space40 />
                    </SubWrapper>
                    <SubWrapper>
                        <Title5>
                            {TranslationHelpers.getFormattedMessage(
                                `${prefix}cancel.membership.subBottomTitle2`,
                            )}
                        </Title5>
                        {prefix === '' && <CancelSpace />}
                        <SubWrapper>
                            <Description>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.overseaServicesline1`,
                                )}
                            </Description>
                            <Description>
                                {TranslationHelpers.getFormattedMessage(
                                    `${prefix}cancel.membership.overseaServicesline2`,
                                )}
                            </Description>
                        </SubWrapper>
                        <CancelSpace />
                        <CheckboxLink
                            id="overseasService"
                            value={isOverseasChecked}
                            checked={isOverseasChecked}
                            label={`${prefix}cancel.membership.checkboxOverseaServices`}
                            onChange={() => {
                                const isOverseasChecked = !this.state.isOverseasChecked;
                                this.setState({ isOverseasChecked });
                                if (isOverseasChecked) {
                                    this.setState({
                                        openOverlayDialog: true,
                                        policyTitleId: POLICY_GLOBAL_TITLE_ID,
                                    });
                                }
                            }}
                        />
                        {overseasError && !isOverseasChecked && overseasError}
                        <Space40 />
                    </SubWrapper>
                </SubWrapper>
                <SubWrapper>
                    <DeleteLinkWrapper>
                        <Button
                            id="cancel"
                            type="button"
                            width="25%"
                            primary={true}
                            text={TranslationHelpers.getMessageDescriptor('button.keepMembership')}
                            onClick={() => {
                                cancelMemberGoBack();
                                history.push('/account-security');
                            }}
                            loading={false}
                            disabled={false}
                            qaId="checkboxJapan"
                            success={false}
                        />
                        <Button
                            width="30%"
                            disabled={false}
                            success={false}
                            text={TranslationHelpers.getMessageDescriptor(
                                'button.delete_cancel_membership',
                            )}
                            onClick={this.openDeleteForm}
                            focusColor={COLORS.white}
                        />
                    </DeleteLinkWrapper>
                </SubWrapper>
            </Wrapper>
        );
    }
}

export default injectIntl(CancelMembership);
